import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Users, Target, Shield, Zap, Award, TrendingUp, Clock, MessageCircle, BarChart3, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCTA from '../components/ProductCTA';

const AscariiExperiencePage = () => {
  const stats = [
    { value: "100%", label: "Dedicated to SAP Business One" },
    { value: "99.995%", label: "Uptime Guarantee" },
    { value: "4.9/5", label: "Customer Satisfaction" },
    { value: "15+", label: "Years Partnership" }
  ];

  const methodology = [
    {
      icon: Target,
      title: "Discovery & Design",
      strapline: "Understand your unique needs",
      desc: "Deep-dive workshops to understand your business processes, challenges, and goals. We design fit-gap analysis, define success metrics, and create a risk mitigation plan tailored to your organisation.",
      features: ["Business process workshops", "Fit-gap analysis", "Success metrics definition", "Risk mitigation planning"]
    },
    {
      icon: Zap,
      title: "Delivery",
      strapline: "PMO discipline, controlled execution",
      desc: "Structured project management with Statement of Work control, comprehensive change management, and detailed training plans. Your business continues running smoothly while we transform it.",
      features: ["PMO project governance", "SoW control and tracking", "Change management", "Comprehensive training"]
    },
    {
      icon: Shield,
      title: "Support",
      strapline: "Proactive monitoring, rapid response",
      desc: "24x7 system monitoring, helpdesk with documented SLAs, release advisory services, and ongoing user enablement. We identify and resolve issues before they impact your operations.",
      features: ["Proactive 24x7 monitoring", "Helpdesk with SLAs", "Release advisory", "User enablement"]
    },
    {
      icon: TrendingUp,
      title: "Ongoing Partnership",
      strapline: "QBRs, KPI tracking, continuous improvement",
      desc: "Quarterly business reviews tracking shared KPIs, optimisation backlogs, and roadmap alignment. We're invested in your long-term success, not just the initial implementation.",
      features: ["Quarterly business reviews", "Shared KPI tracking", "Optimisation planning", "Roadmap co-design"]
    }
  ];

  const faqs = [
    {
      q: "How long does a typical SAP Business One implementation take?",
      a: "For most SMEs, implementation takes 8-12 weeks from project kick-off to go-live. The timeline depends on your business complexity, data migration requirements, and integration needs. We use proven methodologies to minimise disruption and get you live quickly."
    },
    {
      q: "What makes Ascarii different from other SAP Business One partners?",
      a: "We're a partnership-first organisation, not just a supplier. You get dedicated account management, quarterly business reviews with shared KPIs, and proactive support. We're also cloud-first specialists with AWS Advanced Partner status, delivering 99.995% uptime on our hosted platform."
    },
    {
      q: "How do you ensure our data is secure on AWS cloud hosting?",
      a: "Enterprise-grade security with end-to-end encryption, multi-factor authentication, continuous monitoring, and compliance with GDPR and ISO standards. Our AWS infrastructure delivers 99.995% uptime with automated backups and disaster recovery."
    },
    {
      q: "What support SLAs do you offer?",
      a: "We offer tiered support with response times from under 2 hours for critical issues. Our support includes proactive 24x7 monitoring, regular health checks, and dedicated account management. Average customer satisfaction rating: 4.9/5."
    },
    {
      q: "Can you help us switch from our current SAP Business One partner?",
      a: "Yes - partner transfers are straightforward with our proven migration approach. We handle system review, knowledge transfer, and governance setup from day one. Most transfers complete in 4-6 weeks with minimal disruption."
    },
    {
      q: "Do you provide training for our team?",
      a: "Comprehensive training is included in every implementation. We offer role-based training, hands-on workshops, and ongoing user enablement to ensure your team is confident and productive from day one."
    },
    {
      q: "What industries do you specialise in?",
      a: "We have deep expertise in manufacturing (900+ customers with Beas Manufacturing), wholesale distribution (with Produmex WMS), and professional services. Our industry-specific knowledge ensures your implementation follows proven best practices."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="The Ascarii Experience | Partnership-First SAP Business One Journey"
        description="A different kind of SAP partnership. High staff retention, dedicated teams, and we will never leave you. Discover the Ascarii Experience difference."
        keywords="Ascarii Experience, SAP partner experience, ERP partnership, customer-first SAP partner"
        canonical="https://www.ascarii.com/experience"
      />

      {/* Hero */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-blue-700 via-sky-800 to-blue-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&q=85" alt="Partnership" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">The Ascarii Experience</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">A Partnership-First Path to SAP Business One Success</h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">Strategy, delivery, and always-on support - co-owned outcomes, not just implementations. Your success is our mission.</p>
              <div className="flex flex-wrap gap-4 mb-8">
                <a href="/contact">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold shadow-lg">
                    <Play className="w-5 h-5 mr-2" />Book a Discovery Call
                  </Button>
                </a>
                <a href="/contact">
                  <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg font-semibold shadow-lg">
                    <MessageCircle className="w-5 h-5 mr-2" />Talk About Partner Transfer
                  </Button>
                </a>
              </div>
              <div className="flex flex-wrap gap-6 text-white text-sm">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-cyan-300" />
                  <span>SAP Premier Partner</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-cyan-300" />
                  <span>AWS Advanced Tier</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-300" />
                  <span>4.9/5 Customer Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-cyan-300" />
                  <span>99.995% Uptime</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner With Ascarii */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">Partnership Value</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Partner With Ascarii</h2>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200 shadow-lg">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                A <strong>supplier</strong> delivers software and walks away. A <strong>partner</strong> co-owns your success through shared KPIs, regular governance reviews, and continuous roadmap alignment.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <Users className="w-5 h-5 text-blue-600 mr-2" />Dedicated Account Management
                  </h3>
                  <p className="text-sm text-gray-700">Your personal advocate who knows your business, your team, and your goals.</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                    <BarChart3 className="w-5 h-5 text-blue-600 mr-2" />Quarterly Success Reviews
                  </h3>
                  <p className="text-sm text-gray-700">Regular QBRs tracking shared KPIs, reviewing performance, and planning optimisations.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Methodology */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4">Our Approach</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The Ascarii Methodology</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Four pillars of partnership excellence - before, during, and after go-live</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {methodology.map((step, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.1 }} viewport={{ once: true }} className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border-2 border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all">
                <div className="flex items-start gap-6 mb-6">
                  <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-10 h-10 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-blue-600 font-medium mb-3">{step.strapline}</p>
                  </div>
                </div>
                <p className="text-gray-600 mb-4 leading-relaxed">{step.desc}</p>
                <ul className="space-y-2">
                  {step.features.map((feature, i) => (
                    <li key={i} className="flex items-center space-x-2 text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Platform */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <span className="inline-block px-4 py-2 bg-green-100 text-green-600 rounded-full text-sm font-semibold mb-4 w-fit">Cloud Platform</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Cloud Platform You Can Trust</h2>
              <p className="text-lg text-gray-600 mb-6">Cloud-first SAP Business One on AWS with enterprise security, guaranteed scalability, and proven business continuity.</p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">99.995% Uptime Guarantee</h4>
                    <p className="text-sm text-gray-600">AWS Advanced Partner delivering enterprise-grade reliability</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Enterprise Security & Compliance</h4>
                    <p className="text-sm text-gray-600">GDPR compliant with ISO standards, encryption, and continuous monitoring</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Global Scalability</h4>
                    <p className="text-sm text-gray-600">9 AWS datacentre locations with instant resource scaling</p>
                  </div>
                </div>
              </div>
              <div>
                <a href="/cloud-hosting">
                  <Button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 text-base font-semibold shadow-lg">
                    Explore Cloud Hosting for SAP Business One
                  </Button>
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=85" alt="AWS Cloud Infrastructure" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-green-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Transfer */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="relative group flex">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl w-full">
                <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=85" alt="Partner Transfer" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 to-transparent" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="flex flex-col justify-center">
              <span className="inline-block px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4 w-fit">Switching Made Simple</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Partner Transfer - Moving to Ascarii</h2>
              <p className="text-lg text-gray-600 mb-6">Signs you've outgrown your current partner: slow response times, lack of proactive support, no strategic guidance, or feeling like just another ticket number.</p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Low-Risk Migration Plan</h4>
                    <p className="text-sm text-gray-600">Structured transfer process with minimal disruption to operations</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">4-6 Week Timeline</h4>
                    <p className="text-sm text-gray-600">System review, knowledge transfer, governance setup</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Governance from Day One</h4>
                    <p className="text-sm text-gray-600">Immediate account management and proactive support</p>
                  </div>
                </div>
              </div>
              <a href="/contact">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                  <MessageCircle className="w-5 h-5 mr-2" />Start a Partner Transfer Conversation
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">Customer Success</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Proof & Results</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { company: 'Hawthorn Trading', metric: '£13,000 saved', detail: 'First quarter savings through better purchasing power from SAP Business One data visibility' },
              { company: 'Abbeydale Direct', metric: 'Complete transformation', detail: 'From small thinking to making all things possible with Ascarii partnership' },
              { company: 'Hill & Smith Group', metric: 'Multi-subsidiary', detail: 'Standardised ERP across group companies with faster M&A integration' }
            ].map((story, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="bg-white rounded-2xl p-8 border-l-4 border-blue-600 shadow-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">{story.metric}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{story.company}</h3>
                <p className="text-gray-600 text-sm italic">"{story.detail}"</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="/blog">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base font-semibold shadow-lg">
                See More Customer Stories
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white" id="faqs">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4">FAQs</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }} viewport={{ once: true }} className="bg-white p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-700 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Get Your Tailored Roadmap</h2>
          <p className="text-xl text-gray-600 mb-8">Let's discuss how the Ascarii partnership can transform your business</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center rounded-xl bg-blue-600 text-white px-8 py-4 text-lg font-semibold hover:bg-blue-700 shadow-lg transition-colors">
              Get in Touch
            </a>
            <a href="/contact" className="inline-flex items-center justify-center rounded-xl border-2 border-blue-600 text-blue-600 px-8 py-4 text-lg font-semibold hover:bg-blue-50 shadow-lg transition-colors">
              Ask About Switching to Ascarii
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AscariiExperiencePage;
